import React, {useState, useEffect, useRef, useCallback} from 'react';
import {
  Modal,
  View,
  TextInput,
  InputAccessoryView,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  Keyboard,
  TouchableOpacity,
  TouchableWithoutFeedback,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import moment from 'moment';

import Button from '../../elements/Button';
import Divider from '../../elements/Divider';
import ButtonWithIcon from '../../elements/ButtonWithIcon';
import NotesCalendar from '../NotesCalendar';
import TimePicker from '../../elements/TimePicker';
import Text from '../../elements/Text';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {BUTTON_TYPES} from '../../../utils/Constants';
import {trimSpace} from '../../../utils/HelperMethods';
import {tID} from '../../../utils/TestIdHelper';
import {DateIcon, EditIcon, TimeIcon} from '../../../utils/SvgIcons';
import CommonStyles from '../../../utils/CommonStyles';

const AddNewNoteDrawer = ({
  visible,
  note,
  onClose,
  showDateTime,
  buttons = [],
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [title, setTitle] = useState(note.title || 'New Note');
  const [noteText, setNoteText] = useState(note.noteText || '');
  const [readingDate, setReadingDate] = useState();
  const [readingTime, setReadingTime] = useState();
  const [visibleDatePicker, setVisibleDatePicker] = useState(false);
  const [visibleTimePicker, setVisibleTimePicker] = useState(false);
  const [validForm, setValidForm] = useState(false);
  const [validTime, setValidTime] = useState(true);

  const titleRef = useRef(null);

  useEffect(() => {
    setTitle(note.title || 'New Note');
    setNoteText(note.noteText || '');
  }, [note]);

  const getMeasureTime = useCallback(() => {
    let measureTime = new Date().toISOString();
    let dateString = moment().format('YYYY-MM-DD');
    if (readingDate) {
      dateString = readingDate;
    }

    if (readingTime) {
      dateString = dateString + ' ' + readingTime;
    } else {
      dateString = dateString + ' ' + new Date().toTimeString();
    }
    const date = moment(dateString, 'YYYY-MM-DD HH:mm').toDate();
    measureTime = new Date(date.getTime());
    // DLog(
    //   'Add new note drawer',
    //   dateString,
    //   date,
    //   measureTime,
    //   readingDate,
    //   readingTime,
    // );
    return measureTime;
  }, [readingDate, readingTime]);

  useEffect(() => {
    if (trimSpace(title).length == 0) {
      setValidForm(false);
    } else if (trimSpace(noteText).length == 0) {
      setValidForm(false);
    } else {
      if (showDateTime) {
        if (getMeasureTime().getTime() > new Date().getTime()) {
          setValidForm(false);
        } else {
          setValidForm(true);
        }
      } else {
        setValidForm(true);
      }
    }
  }, [title, noteText, showDateTime, getMeasureTime]);

  const onCalendarClose = selectDate => {
    if (selectDate) {
      setReadingDate(selectDate);
    }
    setVisibleDatePicker(false);
  };

  const onTimePickerClose = time => {
    if (time) {
      setReadingTime(time);
    }
    setVisibleTimePicker(false);
  };

  const onPressButton = (button, index) => {
    // if (index == 1) {
    //   if (showDateTime) {
    //     if (getMeasureTime().getTime() > new Date().getTime()) {
    //       setValidTime(false);
    //       return;
    //     } else {
    //       setValidTime(true);
    //     }
    //   }
    // }
    if (button.onPress) {
      button.onPress({
        title,
        noteText,
        createAt: note.createAt,
        readingDateTime: getMeasureTime().toISOString(),
      });
    }
  };

  const renderDateTime = () => {
    const isToday = moment().diff(readingDate, 'days') == 0;
    return (
      <>
        <Text variation="Subtitle1SemiBold" color={colors.textPrimary}>
          Set Date and Time
        </Text>
        <View style={styles.dateTimeView}>
          <ButtonWithIcon
            testID={tID('AddNewNoteDrawer-ButtonWithIcon-Date')}
            style={styles.buttonLeft}
            title={isToday ? 'Today' : moment(readingDate).format('MM/DD/YYYY')}
            icon={<DateIcon />}
            titleColor={colors.textPrimary}
            onPress={() => setVisibleDatePicker(true)}
          />
          <ButtonWithIcon
            testID={tID('AddNewNoteDrawer-ButtonWithIcon-Time')}
            style={styles.buttonRight}
            title={readingTime ? readingTime : 'Now'}
            icon={<TimeIcon />}
            titleColor={colors.textPrimary}
            onPress={() => setVisibleTimePicker(true)}
          />
        </View>
      </>
    );
  };

  return (
    // iOS can't stack Modals — the date/time pickers (each a Modal) are rendered
    // as siblings and this drawer is hidden while a picker is open, so only one
    // Modal is ever visible (prevents the same freeze seen on data export).
    <>
    <Modal
      testID={tID('AddNewNoteDrawer-Modal-Main')}
      transparent
      visible={visible && !visibleDatePicker && !visibleTimePicker}
      onRequestClose={() => onClose?.()}
      animationType="slide">
      <View
        testID={tID('AddNewNoteDrawer-View-Container')}
        style={[
          styles.container,
          Platform.OS === 'web' && styles.webContainer,
        ]}>
        <KeyboardAvoidingView
          keyboardVerticalOffset={-84}
          behavior={Platform.OS == 'ios' ? 'padding' : null}>
          <SafeAreaView
            style={[
              styles.safeareaContainer,
              Platform.OS === 'web' && styles.detailsContainer,
            ]}>
            {/* Standard iOS keyboard dismissal: tapping anywhere in the sheet
                outside a text field dismisses. TextInputs still capture their
                own touches, so caret placement is unaffected. */}
            <TouchableWithoutFeedback
              accessible={false}
              onPress={() => Keyboard.dismiss()}>
            <View style={styles.bottomContainer}>
              <View style={styles.titleContainer}>
                <TextInput
                  testID={tID('AddNewNoteDrawer-TextInput-Title')}
                  ref={titleRef}
                  placeholder="New Note"
                  placeholderTextColor={colors.textPrimary}
                  style={styles.titleInput}
                  value={title}
                  onChangeText={setTitle}
                  returnKeyType="done"
                  blurOnSubmit={true}
                />
                <TouchableOpacity
                  testID={tID('AddNewNoteDrawer-Pressable-EditIcon')}
                  onPress={() => Keyboard.dismiss()}
                  style={styles.editIconButton}>
                  <EditIcon width={24} height={24} color={colors.textOnAccent} />
                </TouchableOpacity>
              </View>
              <Divider top={16} />
              {showDateTime && (
                <View style={styles.dateTimeContainer}>{renderDateTime()}</View>
              )}
              {!validTime && (
                <Text variation="Text1Regular" color={colors.danger} topMargin={8}>
                  Please select a time that is not in the future.
                </Text>
              )}
              <TextInput
                testID={tID('AddNewNoteDrawer-TextInput-Description')}
                placeholder="Type here..."
                placeholderTextColor={colors.textDisabled}
                multiline
                maxLength={160}
                style={styles.noteInput}
                value={noteText}
                onChangeText={setNoteText}
                inputAccessoryViewID="noteInputAccessory"
              />
              {buttons.length > 0 && (
                <View style={styles.buttonsContainer}>
                  {buttons.map((button, index) => (
                    <Button
                      testID={tID(`AddNewNoteDrawer-Button-${button.title}`)}
                      key={index}
                      disabled={button.title === 'Save' && !validForm}
                      style={[
                        styles.button,
                        {marginLeft: index == 0 ? 0 : 16},
                        button.style,
                      ]}
                      buttonType={button.buttonType}
                      titleColor={
                        button.buttonType == BUTTON_TYPES.Filled
                          ? colors.textOnAccent
                          : undefined
                      }
                      title={button.title}
                      onPress={() => onPressButton(button, index)}
                    />
                  ))}
                </View>
              )}
            </View>
            </TouchableWithoutFeedback>
          </SafeAreaView>
        </KeyboardAvoidingView>
        {Platform.OS === 'ios' && (
          <InputAccessoryView nativeID="noteInputAccessory">
            <View style={styles.keyboardAccessory}>
              <TouchableOpacity
                testID={tID('AddNewNoteDrawer-Button-KeyboardDone')}
                onPress={() => Keyboard.dismiss()}
                style={styles.doneButton}>
                <Text variation="Body1SemiBold" color={colors.accentStrong}>
                  Done
                </Text>
              </TouchableOpacity>
            </View>
          </InputAccessoryView>
        )}
      </View>
    </Modal>
    {/* Picker Modals as siblings (never nested in the drawer Modal). */}
    <NotesCalendar
      selectedDate={readingDate && moment(readingDate).format('YYYY-MM-DD')}
      visible={visibleDatePicker}
      onClose={onCalendarClose}
    />
    <TimePicker visible={visibleTimePicker} onClose={onTimePickerClose} />
    </>
  );
};

export default AddNewNoteDrawer;
